import React, { useState } from "react";
import Avatar from "../common/Avatar";
import { getInitials } from "../agency/v2/Avatar";
import ClubVerifiedBadge from "./ClubVerifiedBadge";
import FlagImg from "./FlagImg";
import { InfoGlyph, LoupeGlyph, BookmarkGlyph, CalendarGlyph } from "./glyphs";
import AgentTenderPreviewModal from "./AgentTenderPreviewModal";
import {
  formatPositionValue,
  formatStaffRole,
  formatTypeOfTender,
  formatLeagueTier,
  VERIFICATION_STATUS,
} from "../../constants/agentTenderChoices";
import {
  buildFinRows,
  getCommission,
  getPeriodLine,
  getSpecChips,
} from "../../utils/agentRequestFigures";

// Anonymized feed card for an agent request — V2 (design doc 01, approved
// Marin 2026-07-21).
//
// Brief Rule 4: NO club anywhere — not the logo, name, city, or a link to the
// club profile. The only identities shown are the posting agency and the
// responsible agent. All location/tier data is pre-coarsened by the backend
// (country {name, code}, league_tier) and never resolves the club.
//
// V2 grammar (top to bottom): type pill + verified badge + loupe/bookmark ·
// title + level pill · country line (flag + "England · 1st Tier") · spec chips
// (incl. the "Only free players" info chip) · fin-box with REAL figures
// (budget_band is dead in the UI) + commission three-state · period line ·
// description (2-line clamp) · key requirements (1-line) · agency footer + CTA.

// Type pill colors per the design boards (buy/loan/trial + staff neutrals).
const TYPE_PILL = {
  buy: "bg-[#E8F1FF] text-[#1B6FE0]",
  loan: "bg-[#EEF2FF] text-[#4F46E5]",
  trial: "bg-[#FEF3C7] text-[#B45309]",
};
const typePillClass = (type) => TYPE_PILL[type] || "bg-[#F1F5F9] text-[#334155]";

const levelPillClass = (level) =>
  level === "youth"
    ? "bg-[#E8F1FF] text-[#1B6FE0]"
    : "bg-[#F1F5F9] text-[#475569]";

const formatLevel = (recruiting_for) =>
  recruiting_for === "youth" ? "Youth" : recruiting_for ? "Professional" : null;

// Uppercase micro-label above description / key requirements.
const MicroLabel = ({ children }) => (
  <div className="text-[10px] font-bold tracking-[0.06em] text-[#94A3B8] uppercase mb-0.5">
    {children}
  </div>
);

// Trust line under "Posted by" — the AGENT's record ("14 posted · 11
// verified", red "· 1 revoked" only when > 0). Each label is its own text
// node so the runtime translator can match the words individually. `deals`
// is still tracked in the payload but no longer shown (Marin 2026-08).
export const TrustLine = ({ stats }) => {
  if (!stats) return null;
  const revoked = stats.revoked > 0;
  return (
    // T029-5: `truncate` at every width clipped the line to
    // "3 posted · 1 verified · 0 d…" at 390px, where the single-column card
    // leaves the text column too narrow for one row. Let it WRAP on narrow
    // viewports (the segments break cleanly at the " · " spaces, so every
    // stat survives) and keep the single-line ellipsis from md up, where the
    // row has the width the design assumes.
    <div className="text-[10.5px] text-[#94A3B8] mt-px md:truncate">
      {stats.posted ?? 0} posted{" · "}
      {stats.verified ?? 0} verified
      {revoked && <span className="text-[#B91C1C]">{" · "}{stats.revoked} revoked</span>}
    </div>
  );
};

// The fin-box: real figure rows, "Conditions negotiable" when nothing is set,
// and the commission three-state — never silently blank. Shared markup scale
// between the card and (via props) other dense surfaces.
export const FinBox = ({ tender, unit = "mo" }) => {
  const rows = buildFinRows(tender, unit);
  const commission = getCommission(tender);
  return (
    <div className="bg-[#F8FAFC] border border-[#EEF2F7] rounded-[9px] px-3 py-[9px] flex flex-col gap-[5px]">
      {rows.map((r) => (
        <div key={r.key} className="flex justify-between gap-3 text-[13px]">
          <span className="text-[#64748B]">{r.label}</span>
          <span className="font-semibold text-right tabular-nums text-[#0F172A]">
            {r.value}
          </span>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="text-[13px] text-[#475569]">Conditions negotiable</div>
      )}
      {commission.state === "none" ? (
        <div
          className="flex items-center gap-1 text-[12.5px] font-semibold text-[#B45309]"
          title="The posting agency won't pay or share a commission on this deal."
        >
          No commission for this job
          <InfoGlyph />
        </div>
      ) : (
        <div className="flex justify-between gap-3 text-[13px]">
          <span
            className="inline-flex items-center gap-1 text-[#64748B]"
            title={
              commission.state === "negotiable"
                ? "The split isn't fixed yet · agree it in chat when you respond."
                : undefined
            }
          >
            Commission
            <InfoGlyph className="text-[#94A3B8]" />
          </span>
          <span
            className={`font-semibold ${
              commission.state === "split" ? "text-[#0F172A]" : "text-[#64748B]"
            }`}
          >
            {commission.state === "split" ? commission.value : "Negotiable"}
          </span>
        </div>
      )}
    </div>
  );
};

// Country line: image flag + "England · 1st Tier". The flag is the platform's
// image-flag asset (brief RULE 2 flags exception — emoji flags are removed;
// they rendered as letter pairs on Windows). Card sizing per artboard:
// 18x13, radius 2.5, 1px ring, cover. Absent country renders nothing.
export const CountryLine = ({ tender, className = "" }) => {
  const parts = [tender.country?.name, formatLeagueTier(tender.league_tier)]
    .filter(Boolean)
    .join(" · ");
  if (!parts) return null;
  return (
    <div
      className={`flex items-center gap-[7px] text-[12.5px] text-[#475569] font-medium ${className}`}
    >
      <FlagImg country={tender.country} className="w-[18px] h-[13px] rounded-[2.5px]" />
      {parts}
    </div>
  );
};

// Club-side variant props (Redesign 2.0 SPEC-EXTRACT §9 — the club N2 feed
// renders this SAME card byte-identical by design, plus club deltas). All
// three default to "agent behavior unchanged"; the agent feed passes none of
// them, so its rendering stays pixel-identical (lead ruling 2026-07-22):
//   hideUnverifiedPill  — club side: unverified shows NOTHING, never a
//                         negative pill (v3 principle; warn states still show)
//   cta                 — overrides the footer CTA slot (club: Message agent);
//                         `undefined` keeps the agent Pitch/own-chip logic
//   lastRow             — extra last card row (club: CS1 contacted marker)
//   previewPrimaryAction— overrides the loupe modal's primary action
const AgentTenderCard = ({
  tender,
  onSendPlayer,
  onToggleSave,
  saving = false,
  hideUnverifiedPill = false,
  cta,
  lastRow = null,
  previewPrimaryAction,
}) => {
  const {
    tender_category,
    staff_role,
    position,
    other_staff_position,
    is_saved,
    type_of_tender,
    recruiting_for,
    description,
    key_requirements,
    verification_status,
    posted_agency,
    responsible_agent,
    trust_stats,
    is_own_agency,
    only_free_players,
  } = tender;

  const title =
    tender_category === "staff"
      ? formatStaffRole(staff_role, other_staff_position)
      : formatPositionValue(position, other_staff_position);
  const level = formatLevel(recruiting_for);
  const chips = getSpecChips(tender);
  const period = getPeriodLine(tender);
  const isStaff = tender_category === "staff";

  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm px-4 py-3.5 flex flex-col gap-[9px] min-w-0">
      {/* Badge row: type pill + verification + loupe/bookmark */}
      <div className="flex items-center gap-1.5">
        <span
          className={`text-[11px] font-bold px-[9px] py-[3px] rounded-full capitalize ${typePillClass(
            type_of_tender,
          )}`}
        >
          {formatTypeOfTender(type_of_tender)}
        </span>
        {/* Club side (hideUnverifiedPill): the "Unverified" pill renders
            nothing — emerald stays reserved for Club Verified; superseded/
            revoked warn pills still show. Agent side: unchanged. */}
        {(!hideUnverifiedPill ||
          (verification_status &&
            verification_status !== VERIFICATION_STATUS.UNVERIFIED)) && (
          <ClubVerifiedBadge status={verification_status} />
        )}
        <span className="flex-1" />
        {/* Loupe: read-only anonymized quick preview (Rule 4 — no club). */}
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="w-[26px] h-[26px] border border-[#E2E8F0] rounded-lg inline-flex items-center justify-center text-[#64748B] bg-white hover:bg-gray-50"
          aria-label="Preview request"
          title="Quick preview"
        >
          <LoupeGlyph />
        </button>
        {/* Save / unsave — bookmark; filled + blue when saved. Idempotent. */}
        {onToggleSave && (
          <button
            type="button"
            disabled={saving}
            onClick={() => onToggleSave(tender)}
            className={`w-[26px] h-[26px] rounded-lg inline-flex items-center justify-center border ${
              is_saved
                ? "border-[#BFDBFE] bg-[#E8F1FF] text-[#2283FF]"
                : "border-[#E2E8F0] bg-white text-[#64748B] hover:bg-gray-50"
            } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
            aria-label={is_saved ? "Unsave request" : "Save request"}
            title={is_saved ? "Unsave" : "Save"}
          >
            <BookmarkGlyph saved={!!is_saved} />
          </button>
        )}
      </div>

      {/* Title + level pill */}
      {/* NO `capitalize` on the title: `title` is display-ready already
          (formatStaffRole / formatPositionValue return the choice label, or
          Title-Case the snake_case fallback), and for staff role "other" it is
          the agent's OWN typed title. text-transform:capitalize uppercased
          every word of it — "Head of Set-Piece Analysis" painted as "Head Of
          Set-Piece Analysis", and it broke the enum label "Head of Scouting"
          the same way. The DOM text was always correct; only the CSS lied. */}
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-[16.5px] font-bold text-[#0F172A] tracking-[-0.01em]">
          {title}
        </h3>
        {level && (
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${levelPillClass(
              recruiting_for,
            )}`}
          >
            {level}
          </span>
        )}
      </div>

      {/* Country + tier line (coarse location, never the club) */}
      <CountryLine tender={tender} />

      {/* Spec chips — "Only free players" leads as the emphasized info chip */}
      {(only_free_players || chips.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {only_free_players && (
            <span
              className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#1B6FE0] bg-[#E8F1FF] px-[9px] py-[3px] rounded-full"
              title="Only players without a current contract."
            >
              Only free players
              <InfoGlyph />
            </span>
          )}
          {chips.map((c) => (
            <span
              key={c}
              className="text-[11.5px] font-medium text-[#334155] bg-[#F1F5F9] px-[9px] py-[3px] rounded-full"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* Fin-box: REAL figures + commission three-state (no budget band) */}
      <FinBox tender={tender} />

      {/* Period line — players "For Summer 26" / staff "Starts immediately" */}
      {period && (
        <div className="flex items-center gap-1.5 text-[12px] text-[#64748B]">
          <CalendarGlyph size={12} />
          {period}
        </div>
      )}

      {/* Description — truncated to 2 lines; full text lives in the loupe */}
      {description && (
        <div>
          <MicroLabel>Description</MicroLabel>
          <div className="text-[12px] text-[#475569] leading-normal line-clamp-2">
            {description}
          </div>
        </div>
      )}

      {/* Key requirements — single ellipsized line; full text in the loupe */}
      {key_requirements && (
        <div>
          <MicroLabel>Key requirements</MicroLabel>
          <div className="text-[12px] text-[#475569] truncate">
            {key_requirements}
          </div>
        </div>
      )}

      {/* Agency footer + CTA (NEVER the club) */}
      <div className="border-t border-[#F1F5F9] pt-2.5 mt-auto flex items-end gap-2.5">
        <div className="flex items-center gap-[9px] min-w-0 flex-1">
          <span className="flex-none inline-flex items-center">
            {posted_agency?.logo_url ? (
              <img
                src={posted_agency.logo_url}
                alt={posted_agency?.name || "Agency"}
                className="w-[30px] h-[30px] object-contain rounded-lg bg-white border border-[#E2E8F0]"
                referrerPolicy="no-referrer"
              />
            ) : (
              // T029-1: the fallback used to be a 24px ImageOrInitials while
              // the logo branch above is 30px and the agent avatar that
              // overlaps it (-ml-2 = 8px) is 30px too — so the agent badge
              // covered the smaller disc's centred initial and only a clipped
              // sliver peeked out. Same 30px box as the logo branch: the stack
              // now overlaps identically in both branches.
              <span className="w-[30px] h-[30px] rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] inline-flex items-center justify-center text-[11px] font-bold text-[#64748B]">
                {getInitials(posted_agency?.name)}
              </span>
            )}
            {responsible_agent && (
              <span className="-ml-2 ring-2 ring-white rounded-full flex-none">
                <Avatar
                  src={responsible_agent.avatar}
                  fallbackText={responsible_agent.display_name}
                  size={30}
                />
              </span>
            )}
          </span>
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-[#0F172A] truncate">
              {posted_agency?.name}
            </div>
            {responsible_agent && (
              <div className="text-[11.5px] text-[#64748B] truncate">
                {/* Explicit space node so the runtime translator, which trims
                    the "Posted by" text node, can't glue it to the name. */}
                Posted by{" "}
                {responsible_agent.display_name}
              </div>
            )}
            <TrustLine stats={trust_stats?.agent} />
          </div>
        </div>
        {cta !== undefined ? (
          cta
        ) : is_own_agency ? (
          <span className="flex-none bg-[#E8F1FF] text-[#1B6FE0] text-[12px] font-bold px-3 py-2 rounded-[9px]">
            Your request
          </span>
        ) : (
          onSendPlayer && (
            <button
              type="button"
              onClick={() => onSendPlayer(tender)}
              className="flex-none bg-[#2283FF] text-white text-[12.5px] font-semibold px-3.5 py-2 rounded-[9px] hover:bg-[#1B6FE0]"
            >
              {isStaff ? "Pitch candidate" : "Pitch player"}
            </button>
          )
        )}
      </div>

      {/* Club-side extra last row (CS1 contacted marker, SPEC-EXTRACT §9.3-A).
          Absent on the agent feed. */}
      {lastRow}

      {/* Anonymized read-only preview. variant="feed" guarantees the club is
          never surfaced, no matter what the tender object carries. */}
      <AgentTenderPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        tender={tender}
        variant="feed"
        isOwn={is_own_agency}
        onToggleSave={onToggleSave}
        saving={saving}
        primaryAction={
          previewPrimaryAction !== undefined ? (
            // Function form receives a closer so the override can close the
            // loupe before acting (club flow: close preview → message modal).
            typeof previewPrimaryAction === "function" ? (
              previewPrimaryAction(() => setShowPreview(false))
            ) : (
              previewPrimaryAction
            )
          ) : onSendPlayer && !is_own_agency ? (
            <button
              type="button"
              onClick={() => {
                setShowPreview(false);
                onSendPlayer(tender);
              }}
              className="w-full py-[11px] bg-[#2283FF] text-white text-[13.5px] font-semibold rounded-[10px] hover:bg-[#1B6FE0]"
            >
              {isStaff ? "Pitch candidate" : "Pitch player"}
            </button>
          ) : null
        }
      />
    </div>
  );
};

export default AgentTenderCard;
